import * as countries from 'i18n-iso-countries';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import ConfigQuery from 'Query/Config.query';
import { STORE_CONFIG_KEY } from 'SourceComponent/StoreSwitcher/StoreSwitcher.config';
import {
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps,
    StoreSwitcherContainer as SourceStoreSwitcherContainer
} from 'SourceComponent/StoreSwitcher/StoreSwitcher.container';
import { CustomerType } from 'Type/Account.type';
import { ONE_HOUR_IN_SECONDS } from 'Util/Auth/Token';
import BrowserDatabase from 'Util/BrowserDatabase/BrowserDatabase';
import { noopFn } from 'Util/Common';
import { fetchQueryGet } from 'Util/Request/Query';

/** @namespace PeggysagePwa/Component/StoreSwitcher/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    customer: state.MyAccountReducer.customer
});

/** @namespace PeggysagePwa/Component/StoreSwitcher/Container/mapDispatchToProps */
export const mapDispatchToProps = (state) => ({
    ...sourceMapDispatchToProps(state)
});

countries.registerLocale(require('i18n-iso-countries/langs/fr.json'));
countries.registerLocale(require('i18n-iso-countries/langs/de.json'));
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
countries.registerLocale(require('i18n-iso-countries/langs/es.json'));
countries.registerLocale(require('i18n-iso-countries/langs/nl.json'));

/** @namespace PeggysagePwa/Component/StoreSwitcher/Container */
export class StoreSwitcherContainer extends SourceStoreSwitcherContainer {
    static propTypes = {
        ...SourceStoreSwitcherContainer.props,
        customer: CustomerType.isRequired,
        onBeforeRedirect: PropTypes.func,
        cacheLifetime: PropTypes.number
    };

    static defaultProps = {
        ...SourceStoreSwitcherContainer.defaultProps,
        onBeforeRedirect: null,
        cacheLifetime: ONE_HOUR_IN_SECONDS
    };

    state = {
        ...this.state,
        storeListByWebsite: [],
        currentWebsiteCode: '',
        isLoading: false
    };

    containerFunctions = {
        ...this.containerFunctions,
        handleWebsiteSelect: this.handleWebsiteSelect.bind(this)
    };

    /**
     * Override: Set current website code
     * @param prevProps
     */
    componentDidUpdate(prevProps) {
        const { currentStoreCode, customer: { id: customerId = 0 } = {} } = this.props;
        const { currentStoreCode: prevStoreCode, customer: { id: prevCustomerId = 0 } = {} } = prevProps;
        const { storeLabel, storeList } = this.state;

        if (!storeList.length) {
            this._getStoreList(false);
        }
        if (customerId !== prevCustomerId) {
            this._getStoreList(false);
        }

        if (currentStoreCode && (!storeLabel || (prevStoreCode !== currentStoreCode))) {
            this.getCurrentLabel(currentStoreCode);

            // eslint-disable-next-line no-magic-numbers
            const currentWebsiteCode = currentStoreCode.substr(-2).toLowerCase();
            this.setState({ currentWebsiteCode });
        }
    }

    /**
     * Override: Add store list by websites in props for component
     * @returns {{storeListByWebsite: *[]}}
     */
    containerProps() {
        const { isLoading } = this.state;

        return {
            ...super.containerProps(),
            storeListByWebsite: this.state.storeListByWebsite,
            currentWebsiteCode: this.state.currentWebsiteCode,
            isLoading
        };
    }

    _formatStoreListByWebsite(storeList) {
        const storeListFomatted = this._formatStoreList(storeList);

        const websites = {};

        storeListFomatted.forEach((store) => {
            if (!websites[store.website_code]) {
                const defaultStore = storeList.find(
                    (st) => st.website_code === store.website_code && st.is_default_store
                ) || store;
                const lang = defaultStore.code.substr(0, 2).toLowerCase();

                // eslint-disable-next-line
                let websiteName = countries.getName(store.website_code.toUpperCase(), lang);
                if (!websiteName) {
                    websiteName = store.website_name.replace('PEGGY SAGE ', '').toLowerCase();
                    websiteName = websiteName.charAt(0).toUpperCase() + websiteName.slice(1);
                }

                websites[store.website_code] = {
                    id: `website_${store.website_code}`,
                    label: websiteName,
                    value: store.website_code,
                    stores: [
                        store
                    ],
                    is_hidden: store.is_hidden
                };
            } else {
                websites[store.website_code].stores.push(store);
            }
        });

        // Return array instead of object
        return Object.keys(websites).map((website_code) => websites[website_code]);
    }

    /**
     * Override: Call other function on fetch data success to create list by Website (ie country)
     * @private
     */
    _getStoreList(useCache = false) {
        this.fetchData(
            [ConfigQuery.getStoreListField()],
            ({ storeList }) => {
                this.setState({
                    storeList: this._formatStoreList(storeList),
                    storeListByWebsite: this._formatStoreListByWebsite(storeList)
                });
            },
            noopFn,
            useCache
        );
    }

    /**
     * Override: Add new fields to storelist
     * @param storeList
     * @returns {*}
     * @private
     */
    _formatStoreList(storeList) {
        return storeList.reduce((acc, {
            name, code, is_active, base_url, base_link_url, website_code, website_name, is_hidden/* , is_default_store */
        }) => {
            if (!is_active) {
                return acc;
            }

            return [
                ...acc,
                {
                    id: `store_${ code }`,
                    value: code,
                    storeUrl: base_url,
                    storeLinkUrl: base_link_url,
                    label: name,
                    website_code,
                    website_name,
                    is_hidden
                    // is_default_store
                }
            ];
        }, []);
    }

    handleWebsiteSelect(nextWebsiteCode) {
        const { currentWebsiteCode, storeListByWebsite } = this.state;

        if (nextWebsiteCode !== currentWebsiteCode) {
            const nextWebsite = storeListByWebsite.find(
                ({ value }) => value === nextWebsiteCode
            );

            if (nextWebsite && nextWebsite.stores) {
                const nextStoreCode = nextWebsite.stores[0].value;

                this.handleStoreSelect(nextStoreCode);
            }
        }
    }

    async getTargetUrl(path, storeCode) {
        const { cacheLifetime } = this.props;

        return fetchQueryGet(
            ConfigQuery.getStoreSwitcherUrl(path, storeCode),
            'getStoreSwitcherUrl',
            cacheLifetime
        ).then(
            /** @namespace PeggysagePwa/Component/StoreSwitcher/Container/StoreSwitcherContainer/getTargetUrl/then/catch/fetchQueryGet/then */
            (res) => {
                const { storeSwitcherUrl = {} } = res || {};

                return storeSwitcherUrl;
            }
        ).catch(
            /** @namespace PeggysagePwa/Component/StoreSwitcher/Container/StoreSwitcherContainer/getTargetUrl/then/catch */
            (err) => {
                if (!(err instanceof DOMException && err.name === 'AbortError')) {
                    throw err;
                }

                return {};
            }
        );
    }

    /**
     * Override: add onBeforeRedirect, change location rewrite method
     * @param nextStoreCode
     */
    async handleStoreSelect(nextStoreCode) {
        const { currentStoreCode, showErrorNotification, onBeforeRedirect } = this.props;
        const { storeList } = this.state;

        const currentStore = storeList.find(
            ({ value }) => value === currentStoreCode
        );

        const nextStore = storeList.find(
            ({ value }) => value === nextStoreCode
        );

        if (!nextStore) {
            showErrorNotification(__('This store can not be opened!'));

            return;
        }

        if (onBeforeRedirect) {
            onBeforeRedirect();
        }

        // Get pathname of current url without store code part
        const currentPath = location.pathname.replace(/^\/[a-z]{2}-[a-z]{2}\//i, '');

        if (currentPath) {
            this.setState({ isLoading: true });

            const targetUrl = await this.getTargetUrl(currentPath, nextStoreCode).then(
                /** @namespace PeggysagePwa/Component/StoreSwitcher/Container/StoreSwitcherContainer/handleStoreSelect/targetUrl/then/finally/getTargetUrl/then */
                (result) => {
                    const { url = '' } = result || {};

                    return url;
                }
            ).finally(
                /** @namespace PeggysagePwa/Component/StoreSwitcher/Container/StoreSwitcherContainer/handleStoreSelect/targetUrl/then/finally */
                () => this.setState({ isLoading: false })
            );

            BrowserDatabase.deleteItem(STORE_CONFIG_KEY);

            if (targetUrl) {
                window.location = targetUrl;

                return;
            }
        }

        window.location = window.location.toString().replace(currentStore.storeLinkUrl, nextStore.storeLinkUrl);
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(StoreSwitcherContainer);
